import { v4 as uuidv4 } from 'uuid'
import { CommandPayload, CommandType } from '../types/CommandPayload'
import { computed, ref } from 'vue'
import Bowser from 'bowser'
import { MeetingEvent } from '../types/MeetingEvent'
import { MeetingParticipant } from '../types/MeetingParticipant'
import { IMediaClient } from '../interfaces/IMediaClient'

// Ping monitoring constants
const PING_INTERVAL_MS = 1000 // 1 second
const PING_TIMEOUT_MS = 5000 // 5 seconds
const PING_RECOVERY_WINDOW_MS = 5000 // 5 seconds
const PING_RECOVERY_THRESHOLD = 3 // 3 pings within recovery window
const PING_MONITORING_INTERVAL_MS = 1000 // 1 second

// Snapshot processing constants
const SNAPSHOT_QUEUE_INTERVAL_MS = 500 // 500ms
const SNAPSHOT_REQUEST_TIMEOUT_MS = 15000 // 15 seconds

type SnapshotRequest = CommandPayload & {
    targetUserId: string;
    snapshotId: string;
}

type SnapshotTaken = CommandPayload & {
    snapshotId: string;
}

type SnapshotUploaded = CommandPayload & {
    snapshotId: string;
    token: string;
}

type SnapshotUploadQueueItem = {
    requestingUserId: string;
    blob: Blob;
    snapshotId: string;
}

type SnapshotRequestQueueItem = {
    snapshotId: string;
    status: 'snapshot-requested' | 'snapshot-taken' | 'snapshot-uploaded' | 'snapshot-failed';
    token?: string;
    snapshotUrl?: string;
    thumbnailUrl?: string;
}

abstract class Meeting {

    private _snapshotUploadQueue = ref(new Array<SnapshotUploadQueueItem>())
    private _snapshotRequestQueue = ref(new Array<SnapshotRequestQueueItem>())

    private _mediaClient?: IMediaClient;
    public get mediaClient() : IMediaClient | undefined {
        return this._mediaClient;
    }
    public set mediaClient(mediaClient: IMediaClient) {
        this._mediaClient = mediaClient;
    }

    public constructor(mediaClient?: IMediaClient) {
        this._mediaClient = mediaClient;
    }

    public async init(token: string): Promise<void> {
        this.processSnapshots()
        this.sendPings()
        this.monitorPings()

        return Promise.resolve()
    }

    public get areSnapshotUploadsQueued() {
        return this._snapshotUploadQueue.value.length > 0
    }

    public get isTakingSnapshot() {
        return this._takeSnapshotProcessing
    }

    public get snapshotRequestQueue() {
        return this._snapshotRequestQueue.value
    }
    public get snapshotUploadQueue() {
        return this._snapshotUploadQueue.value
    }

    private _isRequestingSnapshot = computed(() => {
        return this._snapshotRequestQueue.value.some((r) => r.status == 'snapshot-requested')
    })
    public get isRequestingSnapshot() {
        return this._isRequestingSnapshot.value
    }

    private _anySnapshotFailed = computed(() => {
        return this._snapshotRequestQueue.value.some((r) => r.status == 'snapshot-failed')
    })
    public get anySnapshotFailed() {
        return this._anySnapshotFailed.value
    }

    private _lastSnapshotFailed = computed(() => {
        return this._snapshotRequestQueue.value[this._snapshotRequestQueue.value.length-1].status == 'snapshot-failed'
    })
    public get lastSnapshotFailed() {
        return this._lastSnapshotFailed.value
    }

    abstract fire(event: string, payload: any): void;
    abstract on(event: string, callback: (payload: any) => void): void;
    abstract off(event: string, callback: (payload: any) => void): void;

    private _rejectedVisitors = ref(new Array<CommandPayload>())
    public get rejectedVisitors() {
        return this._rejectedVisitors.value
    }

    abstract get participants(): MeetingParticipant[];

    private rejectVisitors(payload: CommandPayload) {
        const visitor = this._rejectedVisitors.value.find((v) => v.senderId == payload.senderId)
        if (!visitor) {
            this._rejectedVisitors.value.push(payload)
        }
        console.log('Visitor rejected', payload.senderName)
    }

    public static get isBrowserSupported(){
        const parser = Bowser.getParser(window.navigator.userAgent);
        const result = parser.getResult();
        if(result.browser.name == 'Safari' && result.browser.version){
            if(parseInt(result.browser.version) < 16) {
                return false
            }
        }
        return true
    }

    /**
     * Detects if the current device is a mobile device
     * @returns An object containing mobile device detection results
     */
    public static detectMobileDevice(): { isMobileDevice: boolean; isDesktopLikeMobileDevice: boolean } {
        const isSimpleMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const iPadPro = !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform))
        
        if (isSimpleMobileDevice || iPadPro) {
            return {
                isMobileDevice: true,
                isDesktopLikeMobileDevice: iPadPro
            }
        } else {
            return {
                isMobileDevice: false,
                isDesktopLikeMobileDevice: false
            }
        }
    }

    public handleCommand(payload: CommandPayload) {
        //console.log(`Command from ${payload.senderName} is ${payload.commandType}`);
        switch (payload.commandType) {
            case 'snapshot-requested':
                this.takeSnapshot(payload as SnapshotRequest)
                break
            case 'snapshot-taken':
                this.updateSnapshotStatus((payload as SnapshotTaken).snapshotId, 'snapshot-taken', payload)
                break
            case 'snapshot-uploaded':
                this.updateSnapshotStatus((payload as SnapshotUploaded).snapshotId, 'snapshot-uploaded', payload)
                break
            case 'visitor-rejected':
                this.rejectVisitors(payload)
                break
            case 'ping':
                this.recordPing(payload)
                break
            default:
                console.log('Uhhandled command type', payload.commandType)
        }

    }

    abstract sendCommand(payload: CommandPayload, to?: string): Promise<void>;
    abstract sendCommandFast(commandType: string, to?: string): void;
    abstract getName(): string;
    abstract getUserId(): string;
    abstract getRoomId(): string;
    abstract getSessionId(): string;

    abstract setSpotlight(userId: string): Promise<void>;
    abstract removeSpotlight(userId: string): Promise<void>;

    private _pings = ref(new Map<string, number>())
    public recordPing(payload: CommandPayload) {
        this._pings.value.set(payload.senderId, Date.now())
        
        // If user is in not responding list, track their ping count for potential removal
        if (this._notRespondingUsers.value.includes(payload.senderId)) {
            this._trackPingForRemoval(payload.senderId)
        }
    }

    // Track ping counts for users in the not responding list
    private _pingCountsForRemoval = ref(new Map<string, { count: number, firstPingTime: number }>())
    
    private _trackPingForRemoval(userId: string) {
        const now = Date.now()
        const existing = this._pingCountsForRemoval.value.get(userId)
        
        if (!existing) {
            // First ping after being marked as not responding
            this._pingCountsForRemoval.value.set(userId, { count: 1, firstPingTime: now })
        } else {
            // Check if we're within the 5-second window
            if (now - existing.firstPingTime <= PING_RECOVERY_WINDOW_MS) {
                existing.count++
                
                // If we've received 3 pings within 5 seconds, remove from not responding list
                if (existing.count >= PING_RECOVERY_THRESHOLD) {
                    this._removeFromNotRespondingList(userId)
                }
            } else {
                // Reset the counter if outside the 5-second window
                this._pingCountsForRemoval.value.set(userId, { count: 1, firstPingTime: now })
            }
        }
    }
    
    private _removeFromNotRespondingList(userId: string) {
        const index = this._notRespondingUsers.value.indexOf(userId)
        if (index > -1) {
            this._notRespondingUsers.value.splice(index, 1)
            this._pingCountsForRemoval.value.delete(userId)
            console.log(`User ${userId} is responding again, removed from not responding list`)
            this.fire(MeetingEvent.UserResponding, { userId: userId })
        }
    }

    // Clean up ping tracking when a user leaves the meeting
    public cleanupUserPingTracking(userId: string) {
        this._pings.value.delete(userId)
        this._pingCountsForRemoval.value.delete(userId)
        const index = this._notRespondingUsers.value.indexOf(userId)
        if (index > -1) {
            this._notRespondingUsers.value.splice(index, 1)
        }
    }

    public sendPing(): void {
        this.sendCommandFast(CommandType.Ping)
    }

    private _snapshotQueueInterval: NodeJS.Timeout | null = null
    public processSnapshots(){
        this._snapshotQueueInterval = setInterval(() => {
            this.processSnapshotUploadQueue()
        }, SNAPSHOT_QUEUE_INTERVAL_MS)
    }

    private _sendPingsInterval: NodeJS.Timeout | null = null;
    public sendPings(): void {
        if(this._sendPingsInterval){
            clearInterval(this._sendPingsInterval)
        }
        this._sendPingsInterval = setInterval(() => {
            this.sendPing();
        }, PING_INTERVAL_MS); // Send ping every second
    }

    private _notRespondingUsers = ref(new Array<string>())
    public get notRespondingUsers() {
        // two lists:
        // list a is the list of users not responding
        // list b is the users that are still "in the meeting" (participants list)
        // we want to return the intersection of these two lists, which is users in the meeting that are not responding
        return this._notRespondingUsers.value.filter((user) => {
            return this.participants.some((participant) => participant.userId === user);
        });
    }

    private _monitorPingsInterval: NodeJS.Timeout | null = null;
    public monitorPings() {
        this._monitorPingsInterval = setInterval(() => {
            const now = Date.now();
            const usersToCheck = Array.from(this._pings.value.entries())
            
            // Process each user individually to avoid issues with same timestamps
            usersToCheck.forEach(([userId, timestamp]) => {
                if (now - timestamp > PING_TIMEOUT_MS) { // 5 seconds since last ping
                    console.warn(`User ${userId} is not responding`)
                    // delete the user from the pings map
                    this._pings.value.delete(userId);
                    
                    // Only add to not responding list if not already there AND user is in participants list
                    if (!this._notRespondingUsers.value.includes(userId) && 
                        this.participants.some((participant) => participant.userId === userId)) {
                        this._notRespondingUsers.value.push(userId);
                        // fire an event
                        this.fire(MeetingEvent.UserNotResponding, { userId: userId });
                        console.log(`Added user ${userId} to not responding list`)
                    }
                }
            });
        }, PING_MONITORING_INTERVAL_MS); // Check every second
    }

    public requestSnapshot(from: string): SnapshotRequest | undefined {
        const payload = {
            commandType: 'snapshot-requested',
            senderId: this.getUserId(),
            senderName: this.getName(),
            snapshotId: uuidv4(),
            targetUserId: from
        } as SnapshotRequest
        this.sendCommand(payload, from) // ignoring async
        this._snapshotRequestQueue.value.push({
            snapshotId: payload.snapshotId,
            status: 'snapshot-requested',
        } as SnapshotRequestQueueItem)

        window.setTimeout(() => {
            const requestQueueItem = this._snapshotRequestQueue.value.find((r) => r.snapshotId == payload.snapshotId && r.status == 'snapshot-requested')
            if (requestQueueItem) {
                // remove the request after 5 seconds if it still exists
                console.log('Snapshot request timed out, marking as failed')
                requestQueueItem.status = 'snapshot-failed'
            }
        }, SNAPSHOT_REQUEST_TIMEOUT_MS)
        return payload
    }

    public notifySnapshotTaken(to: string, snapshotId: string): SnapshotTaken | undefined {
        const payload = {
            commandType: 'snapshot-taken',
            senderId: this.getUserId(),
            senderName: this.getName(),
            snapshotId: snapshotId
        } as SnapshotTaken
        this.sendCommand(payload, to) // ignoring async
        return payload
    }

    public notifySnapshotUploaded(to: string, snapshotId: string, token: string): SnapshotUploaded | undefined {
        const payload = {
            commandType: 'snapshot-uploaded',
            senderId: this.getUserId(),
            senderName: this.getName(),
            token: token,
            snapshotId: snapshotId
        } as SnapshotUploaded
        this.sendCommand(payload, to) // ignoring async
        return payload
    }

    abstract get latestMediaStream(): MediaStream | null;

    abstract getSnapshot(): Promise<Blob | null>;

    async destroy(willRebuild: boolean): Promise<void> {
        if (this._sendPingsInterval) {
            clearInterval(this._sendPingsInterval);
            this._sendPingsInterval = null;
        }
        if (this._monitorPingsInterval) {
            clearInterval(this._monitorPingsInterval);
            this._monitorPingsInterval = null;
        }
        if (this._snapshotQueueInterval) {
            clearInterval(this._snapshotQueueInterval);
            this._snapshotQueueInterval = null;
        }
        
        // Clean up ping tracking maps
        this._pings.value.clear()
        this._pingCountsForRemoval.value.clear()
        this._notRespondingUsers.value.length = 0
        
        // cleanup any resources, listeners, etc.
        console.log('Meeting destroyed', willRebuild);
    }

    private _takeSnapshotQueue = ref(new Array<SnapshotRequest>())
    private _takeSnapshotProcessing = false
    private _count = 0
    private takeSnapshot(payload: SnapshotRequest) {
        // can't take multiple snapshots at the same time
        if (this._takeSnapshotProcessing) {
            console.log('Snapshot already in progress, queueing snapshot')
            this._takeSnapshotQueue.value.push(payload)
            return
        }
        
        this._count++
        /*
        this is useful for debugging - causes a timeout every 2nd snapshot
        if(this._count %2 == 0) {
            return
        }*/

        this._takeSnapshotProcessing = true
        this.getSnapshot()
            .then((blob) => {
                this._snapshotUploadQueue.value.push({
                    requestingUserId: payload.senderId,
                    blob: blob as Blob,
                    snapshotId: payload.snapshotId
                })
                this.notifySnapshotTaken(payload.senderId, payload.snapshotId)
            }).finally(() => {
                this._takeSnapshotProcessing = false
                if (this._takeSnapshotQueue.value.length > 0) {
                    const nextSnapshot = this._takeSnapshotQueue.value.shift()
                    if (nextSnapshot) {
                        // deferring the next snapshot to allow the current one to clean up
                        window.setTimeout(() => {
                            this.takeSnapshot(nextSnapshot)
                        }, 1)
                    }
                }
            })
    }


    private _snapshotUploading = false
    private async processSnapshotUploadQueue() {

        if (this._snapshotUploadQueue.value.length == 0) return
        if (this._snapshotUploading) return
        if (!this._mediaClient) {
            console.error('Media client is not set, cannot upload snapshot')
            return
        }
        this._snapshotUploading = true

        const queueItem = this._snapshotUploadQueue.value.shift()
        try {
            if (!queueItem) return
            const snapshot = await this._mediaClient.createSnapshot(this.getRoomId(), this.getSessionId(), queueItem.blob as Blob)
            const uploadResult = snapshot.uploadResult;
            if(!uploadResult) {
                console.error('Snapshot upload result is missing', snapshot);
            }else{
                this.notifySnapshotUploaded(queueItem.requestingUserId, queueItem.snapshotId, uploadResult.token)
            }
        } catch (e) {
            console.error('Error uploading snapshot', e)
            if (queueItem) {
                this._snapshotUploadQueue.value.unshift(queueItem)
            }
        } finally {
            this._snapshotUploading = false
        }
    }

    private async updateSnapshotStatus(snapshotId: string, status: 'snapshot-taken' | 'snapshot-uploaded', payload: CommandPayload) {
        const requestQueueItem = this._snapshotRequestQueue.value.find((r) => r.snapshotId == snapshotId)
        if (!requestQueueItem) {
            console.log('No original request found for snapshot', snapshotId)
            return
        }
        requestQueueItem.status = status
        if (status == 'snapshot-uploaded') {
            const snapshot = payload as SnapshotUploaded
            requestQueueItem.token = snapshot.token

            const urls = await this.getSnapshotUrls(requestQueueItem)
            if (!urls) {
                console.error('Failed to get snapshot URLs for', requestQueueItem.snapshotId)
                requestQueueItem.status = 'snapshot-failed'
                return
            }
            console.log('Snapshot', urls.snapshotUrl)
        }
    }

    private async getSnapshotUrls(snapshot: SnapshotRequestQueueItem) {
        if (!this._mediaClient) {
            console.error('Media client is not set, cannot get snapshot URLs')
            return null
        }
        const artifact = await this._mediaClient.getSnapshot(snapshot.token!)
        snapshot.thumbnailUrl = artifact.thumbnailUrl
        snapshot.snapshotUrl = artifact.url
        return {
            thumbnailUrl: artifact.thumbnailUrl,
            snapshotUrl: artifact.url
        }
    }
}

export default Meeting
export { Meeting }